import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getToken } from "../utils/tokenStorage";
import { getUserRole } from "../store/userRole";
import { getStudentRedirectUrl } from "../utils/redirectStorage";
import Loader from "./Loader";

const PublicRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const hasNavigated = useRef(false); // Prevent multiple navigations
  const navigate = useNavigate();
  const location = useLocation();
  const roleOfTheUser = useSelector(getUserRole);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = await getToken();
        const isAuthenticated = token !== null;

        if (isAuthenticated && roleOfTheUser && !hasNavigated.current) {
          hasNavigated.current = true; // Mark that we're navigating

          // Extract redirect URL from query params (same as login logic)
          const queryParams = new URLSearchParams(location.search);
          const redirectUrl = queryParams.get("redirect");

          // Define admin/teacher roles that should go to admin interface (same as login logic)
          const adminRoles = [
            "Admin",
            "SuperAdmin",
            "Root",
            "Teacher",
            "Watcher",
          ];
          const isAdminUser =
            adminRoles.includes(roleOfTheUser) || roleOfTheUser !== "Student";

          if (isAdminUser) {
            navigate("/myClassrooms", { replace: true });
          } else {
            // For students, check if login just happened
            // If so, don't do anything - LoginForm already handled navigation
            const loginJustHappened =
              sessionStorage.getItem("login-in-progress");

            if (loginJustHappened === "true") {
              sessionStorage.removeItem("login-in-progress"); // Clean up
              setIsChecking(false);
              return;
            }

            if (redirectUrl !== null) {
              const fullRedirectUrl = `/${redirectUrl}`;
              navigate(fullRedirectUrl, { replace: true });
            } else {
              navigate("/student/myClassrooms", { replace: true });
            }
          }
        } else {
          // Not authenticated or role not loaded yet, allow access to public route
          setIsChecking(false);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        setIsChecking(false);
      }
    };

    checkAuthStatus();
  }, [navigate, location.search, roleOfTheUser]);

  if (isChecking) {
    return <Loader />;
  }

  return children;
};

export default PublicRoute;
